import {
    type ManagedPlaylistController,
    PlaylistControllerImpl,
} from '@/playback/PlaylistController'
import {
    type Container,
    createContainer,
    type Factories,
    validateFactories,
} from '@amazon/vinyl-di'
import {
    type CloudQueueControllerDeps,
    CloudQueueControllerImpl,
} from '@/cloudQueue/CloudQueueController'
import { bindPodcastRepeatOff } from '@/cloudQueue/podcastRepeatGuard'
import type { AppViewDeps } from '@/AppView'
import { createPlayer } from '@/playback/playerFactory'
import { ctx } from '@/util/context'
import { createGoodNetworkObservable } from '@/offline/createGoodNetworkObservable'
import {
    PartySessionControllerImpl,
    type PartySessionControllerImplDeps,
} from '@/party/PartySessionController'

/**
 * Interface for application dependencies with lazy-loaded player
 */
export type AppDependencies = AppViewDeps
const { errorHandler } = ctx('appDependencies')

export function createAppDependencies(): Container<AppDependencies> {
    return createContainer(
        validateFactories({
            playerPromise: (
                deps: { readonly playlistController: ManagedPlaylistController },
                add
            ) => createPlayer({ deps, add, errorHandler }),

            playlistController: (): ManagedPlaylistController => new PlaylistControllerImpl(),

            cloudQueueController: (deps: CloudQueueControllerDeps, add) => {
                const controller = new CloudQueueControllerImpl(deps)
                add(
                    bindPodcastRepeatOff({
                        playlistController: deps.playlistController,
                        modeWriter: controller.modeWriter,
                    })
                )
                return controller
            },

            goodNetwork$: createGoodNetworkObservable,

            partySessionController: (deps: PartySessionControllerImplDeps) =>
                new PartySessionControllerImpl(deps),
        } as const satisfies Factories<AppViewDeps>)
    )
}
